// The public addresses of the Hub (LH-L, owner 2026-10-17: "where can I copy these endpoints, like Postman, UAT and
// production"). Pure functions: the API catalogue, the copy buttons on 3.1, the Postman and OpenAPI exports use them.
//
//   {address}/lenders/{slug}{route}      one endpoint of the lender's batch, e.g. …/lenders/moneyview/v2/dedupe
//                                        header x-hub-key; body { input: applicant, prev? }; answer = the endpoint's result
//   {address}/lenders/{slug}/_hub/health the batch's health (the runner's check)
//   {address}/v1/applications            the service endpoint the web app calls: body { endpoint_id, applicant }, header x-app-key
//
// The addresses themselves are Settings → Hub addresses (PortalSettings.hubUrls); the backend serves them (api-contract.md).
import type { Batch, Lender, ManifestEndpoint, PortalSettings, ServiceEndpoint } from '@/types'

export type HubEnv = 'uat' | 'production'
export const DEFAULT_HUB_URLS: PortalSettings['hubUrls'] = { uat: 'https://hub-uat.firstfundfincorp.com', production: 'https://hub.firstfundfincorp.com' }

export const HUB_KEY_HEADER = 'x-hub-key'
export const APP_KEY_HEADER = 'x-app-key'

const trim = (base: string) => base.replace(/\/+$/, '')

/** The address of one endpoint of a lender's batch. */
export const endpointUrl = (base: string, slug: string, route: string) => `${trim(base)}/lenders/${slug}${route.startsWith('/') ? route : `/${route}`}`
export const healthUrl = (base: string, slug: string) => `${trim(base)}/lenders/${slug}/_hub/health`
/** The service endpoint (3.5): one address for every lender and product; endpoint_id goes in the body. */
export const serviceUrl = (base: string) => `${trim(base)}/v1/applications`

/** The body a lender endpoint takes: the applicant as `input` (ctx.prev for a status / offers step). */
export const endpointBody = (applicant: Record<string, unknown>, prev?: Record<string, unknown> | null) => (prev ? { input: applicant, prev } : { input: applicant })
export const serviceBody = (endpointId: string, applicant: Record<string, unknown>) => ({ endpoint_id: endpointId, applicant })

/** A curl line someone can paste: the key stays a placeholder, never a value. */
export function curlFor(method: string, url: string, body: unknown | null, keyHeader: string): string {
  const parts = [`curl -X ${method} '${url}'`, `-H '${keyHeader}: <your key>'`]
  if (body !== null && method !== 'GET') parts.push(`-H 'Content-Type: application/json'`, `-d '${JSON.stringify(body)}'`)
  return parts.join(' \\\n  ')
}

/** The batch the catalogue shows for a lender: the live one, else the newest. */
export function shownBatch(batches: Batch[], slug: string): Batch | undefined {
  const mine = batches.filter((b) => b.lenderSlug === slug)
  return mine.find((b) => b.status === 'live') ?? mine.slice().sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true }))[0]
}

export interface CatalogueRow {
  lender: Lender
  batch: Batch
  endpoint: ManifestEndpoint
  uat: string
  /** null when the lender has no live batch yet */
  production: string | null
}

export function catalogueRows(lenders: Lender[], batches: Batch[], urls: PortalSettings['hubUrls']): CatalogueRow[] {
  const out: CatalogueRow[] = []
  for (const lender of lenders) {
    const batch = shownBatch(batches, lender.slug)
    if (!batch) continue
    const live = batch.status === 'live'
    for (const endpoint of batch.manifest.endpoints) out.push({ lender, batch, endpoint, uat: endpointUrl(urls.uat, lender.slug, endpoint.path), production: live ? endpointUrl(urls.production, lender.slug, endpoint.path) : null })
  }
  return out
}

// ---- Postman -------------------------------------------------------------------------------------------------------

const pmUrl = (raw: string) => {
  const u = new URL(raw.replace('{{hubUrl}}', 'https://hub.example'))
  return { raw, host: ['{{hubUrl}}'], path: u.pathname.split('/').filter(Boolean) }
}
const pmHeaders = (keyHeader: string) => [{ key: keyHeader, value: '{{hubKey}}', type: 'text' }, { key: 'Content-Type', value: 'application/json', type: 'text' }]
const pmBody = (body: unknown) => ({ mode: 'raw', raw: JSON.stringify(body, null, 2), options: { raw: { language: 'json' } } })

/** A Postman collection (v2.1): one folder per lender with its endpoints, one folder for the service endpoints. `{{hubUrl}}` and `{{hubKey}}` come from the environment file. */
export function postmanCollection(lenders: Lender[], batches: Batch[], endpoints: ServiceEndpoint[], applicant: Record<string, unknown>) {
  const folders = lenders.map((lender) => {
    const batch = shownBatch(batches, lender.slug)
    return {
      name: `${lender.name} · batch ${batch?.version ?? '—'}`,
      description: batch ? `${batch.status.replace('_', ' ')}; outcomes per endpoint in each request's description` : 'no batch yet',
      item: (batch?.manifest.endpoints ?? []).map((e) => ({
        name: `${e.method} ${e.path}${e.title ? ` · ${e.title}` : ''}`,
        request: {
          method: e.method,
          header: pmHeaders(HUB_KEY_HEADER),
          url: pmUrl(`{{hubUrl}}/lenders/${lender.slug}${e.path}`),
          ...(e.method === 'GET' ? {} : { body: pmBody(endpointBody(applicant)) }),
          description: `Outcomes: ${e.outcomes.join(' · ')}${e.tokenSource ? '. Token source: the Gateway calls it, workflows do not.' : ''}`,
        },
      })),
    }
  })
  const service = {
    name: 'Service endpoints (what the web app calls)',
    item: endpoints.filter((e) => e.state !== 'retired').map((e) => ({
      name: `${e.id} (${e.lenderSlug} · ${e.env === 'uat' ? 'UAT only' : 'UAT + production'})`,
      request: { method: 'POST', header: pmHeaders(APP_KEY_HEADER), url: pmUrl('{{hubUrl}}/v1/applications'), body: pmBody(serviceBody(e.id, applicant)), description: `Workflow ${e.workflowName} ${e.workflowVersion}; the standard answer comes back` },
    })),
  }
  return {
    info: { name: 'Firstfund Lender Hub', description: 'Every lender endpoint and the service endpoints. Pick the UAT or the production environment; set hubKey there (never in this file).', schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json' },
    variable: [{ key: 'hubUrl', value: DEFAULT_HUB_URLS.uat }, { key: 'hubKey', value: '' }],
    item: [...folders, service],
  }
}

/** A Postman environment: the address of one environment; the key is left for the person to fill. */
export const postmanEnvironment = (env: HubEnv, urls: PortalSettings['hubUrls']) => ({
  name: `Lender Hub · ${env === 'uat' ? 'UAT' : 'Production'}`,
  values: [
    { key: 'hubUrl', value: urls[env], enabled: true, type: 'default' },
    { key: 'hubKey', value: '', enabled: true, type: 'secret' },
  ],
  _postman_variable_scope: 'environment',
})

// ---- OpenAPI -------------------------------------------------------------------------------------------------------

/** OpenAPI 3.0 of the same set: both servers, one path per endpoint, the key header as the security scheme. */
export function openApiFor(lenders: Lender[], batches: Batch[], endpoints: ServiceEndpoint[], urls: PortalSettings['hubUrls']) {
  const paths: Record<string, Record<string, unknown>> = {}
  for (const row of catalogueRows(lenders, batches, urls)) {
    const p = `/lenders/${row.lender.slug}${row.endpoint.path}`
    paths[p] = {
      ...(paths[p] ?? {}),
      [row.endpoint.method.toLowerCase()]: {
        tags: [row.lender.name],
        summary: row.endpoint.title ?? row.endpoint.path,
        description: `Batch ${row.batch.version} (${row.batch.status.replace('_', ' ')}). Outcomes: ${row.endpoint.outcomes.join(' · ')}.${row.production ? '' : ' Not live yet: UAT only.'}`,
        ...(row.endpoint.method === 'GET' ? {} : { requestBody: { required: true, content: { 'application/json': { schema: { $ref: '#/components/schemas/EndpointInput' } } } } }),
        responses: { '200': { description: 'The endpoint result: { ok, outcome, value, error, issues, calls, log, envMissing, ms }' }, '404': { description: 'no such endpoint' } },
      },
    }
  }
  if (endpoints.some((e) => e.state !== 'retired')) {
    paths['/v1/applications'] = {
      post: {
        tags: ['Service endpoints'],
        summary: 'Send an applicant to a lender through a service endpoint (endpoint_id in the body)',
        description: `endpoint_id is one of: ${endpoints.filter((e) => e.state !== 'retired').map((e) => e.id).join(', ')}`,
        security: [{ appKey: [] }],
        requestBody: { required: true, content: { 'application/json': { schema: { type: 'object', required: ['endpoint_id', 'applicant'], properties: { endpoint_id: { type: 'string' }, applicant: { type: 'object' } } } } } },
        responses: { '200': { description: 'The standard answer (status, lender_ref, offers, links, message, lender_reply)' }, '404': { description: 'endpoint_id unknown or retired' }, '409': { description: 'lender or product switched off' } },
      },
    }
  }
  return {
    openapi: '3.0.3',
    info: { title: 'Firstfund Lender Hub', version: new Date().toISOString().slice(0, 10), description: 'Every lender endpoint of the live (or newest) batch and the service endpoints the web app calls.' },
    servers: [{ url: urls.uat, description: 'UAT' }, { url: urls.production, description: 'Production' }],
    security: [{ hubKey: [] }],
    paths,
    components: {
      securitySchemes: { hubKey: { type: 'apiKey', in: 'header', name: HUB_KEY_HEADER }, appKey: { type: 'apiKey', in: 'header', name: APP_KEY_HEADER } },
      schemas: { EndpointInput: { type: 'object', required: ['input'], properties: { input: { type: 'object', description: 'the applicant (the standard input)' }, prev: { type: 'object', description: 'the earlier step’s outcome (status / offers endpoints)' } } } },
    },
  }
}

/** Downloads a JSON file in the browser; where that is not possible (tests), puts the text on the clipboard. */
export async function downloadJson(name: string, value: unknown): Promise<'downloaded' | 'copied'> {
  const text = JSON.stringify(value, null, 2)
  if (typeof URL.createObjectURL === 'function' && typeof document !== 'undefined') {
    const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }))
    const a = document.createElement('a')
    a.href = url
    a.download = name
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    return 'downloaded'
  }
  await navigator.clipboard.writeText(text)
  return 'copied'
}
